import InfoFillIcon from 'assets/icons/info-fill-icon.svg';
import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import {
  findDiscountDetails,
  getDiscountsActiveList,
  handleRemoveDiscountApplied,
} from 'components/Discount/func';
import CloseIcon from 'components/common/Icons/CloseIcon';
import InformPopup from 'components/common/InformPopup';
import {
  DESKTOP_MIN_WIDTH,
  EN_LANG,
  MOBILE_SMALL_MAX_WIDTH,
  MOBILE_SMALL_MIN_WIDTH,
  VI_LANG,
} from 'constants';
import { format } from 'helpers';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useAutoScaleToFitContainer from 'hooks/useAutoScaleToFitContainer';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DiscountModal } from '../DiscountModal/ver2';
import CircleRoundCutHorizontal from './CircleRoundCutHorizontal';
import usePage from 'hooks/usePage';
import {
  cursorPointerMixin,
  displayFlexColStartCenterMixin,
  displayGridCenterStartMixin,
  fontSize12Mixin,
  paragraph500Mixin,
  paragraph700Mixin,
} from 'styles';
import SizeBox from 'components/common/SizeBox';
import GiftBoxIcon from 'assets/ver2/icons/gift-box-icon.svg';
import { numberUtils } from 'utils';
import { preventOnClick } from 'components/common/func';

const DiscountApplied = ({
  isFx,
  isMakePayment,
  onToggleNavbar,
  onDismissEligibilityToast,
}) => {
  const { t, locate = EN_LANG } = useLang();
  const { state } = useStore();
  let { addTransactionInformation } = state;

  const { addTransactionInformation: fxAddTransactionInformation } =
    useFxFormAddTransactionStore();

  addTransactionInformation = isFx
    ? fxAddTransactionInformation
    : addTransactionInformation;

  const { paymentMethod } = addTransactionInformation || {};
  const { isDesktop, isSmallMobile } = useDetectDevice();

  const {
    discountsAvailable,
    discountsInactive,
    discountApplied,
    discountDetails,
  } = paymentMethod || {};

  const { code, discountAmount } = discountApplied || {};
  const discountsActive = getDiscountsActiveList({
    discountsAvailable,
    discountsInactive,
  });
  const { isAddTransactionPage } = usePage();

  const [discount, setDiscount] = useState(null);
  const [isOpenDiscountModal, setOpenDiscountModal] = useState(false);
  const [isOpenRemoveConfirm, setOpenRemoveConfirm] = useState(false);

  const discountCodeContainerWidth = useMemo(() => {
    if (isDesktop) return 280;

    if (isSmallMobile) return 130;

    return 160;
  }, []);

  const discountCodeClassName = `discount-applied-code-${code}`;
  useAutoScaleToFitContainer({
    className: discountCodeClassName,
    maxWidth: discountCodeContainerWidth,
    alignHorizontal: 'left',
  });

  const handleOnCloseDiscountModal = (e) => {
    preventOnClick(e);
    setOpenDiscountModal(false);
  };

  const handleOnOpenRemoveConfirm = (e) => {
    preventOnClick(e);
    setOpenRemoveConfirm(true);
  };

  const handleOnCloseRemoveConfirm = () => {
    setOpenRemoveConfirm(false);
  };

  const handleOnConfirmRemoveDiscount = () => {
    handleRemoveDiscountApplied();
    // Soft-dismiss the discount eligibility/apply toast on removal, matching
    // the amount-change dismiss in Remittance (setShowDiscountEligibilityToast
    // (false)) threaded down here via onDrawerOpen.
    onDismissEligibilityToast?.();
    setOpenRemoveConfirm(false);
  };

  const handleOnOpenDiscountModal = () => {
    setOpenDiscountModal(true);

    // Show the details of the *applied* discount. Prefer its full definition
    // from the active list (a successful apply leaves the item in
    // discountsAvailable). The stored discountDetails is not kept in sync with
    // a successful apply -- it can hold a previously-failed discount -- so only
    // trust it when its code matches the applied one (e.g. the
    // make-payment/update flow where the active list is not loaded).
    const appliedDetails = findDiscountDetails(discountsActive, code);
    if (appliedDetails) {
      setDiscount(appliedDetails);
    } else if (discountDetails?.code === code) {
      setDiscount(discountDetails);
    } else {
      setDiscount(null);
    }
  };

  // Remove-button visibility depends on the applied discount's own `removable`
  // field (removable -> show, non-removable -> hide). It must not key off a
  // re-derived default discount, whose presence/removable shifts with the
  // amount-entry/clear/re-derive sequence and made the button flicker between
  // identical applies of the same discount. Resolve `removable` from the applied
  // discount's definition in the active list (matched by its own code -- the
  // same source the details modal trusts), because a manual apply stores only
  // the /discounts apply response in discountApplied, which does not carry
  // `removable`; fall back to the applied object for flows without the list.
  const appliedDiscountDetails = findDiscountDetails(discountsActive, code);
  const isAppliedDiscountRemovable = appliedDiscountDetails
    ? appliedDiscountDetails.removable
    : discountApplied?.removable;
  const isPreventRenderRemoveDiscountButton = Boolean(
    isMakePayment || (!isEmpty(discountApplied) && !isAppliedDiscountRemovable),
  );
  const renderRemoveDiscountButton = () => {
    if (isPreventRenderRemoveDiscountButton) return null;
    if (isAddTransactionPage)
      return (
        <>
          <SizeBox height={4} />
          <RemoveButton onClick={handleOnOpenRemoveConfirm}>
            {t('button_remove_discount')}
          </RemoveButton>
          <SizeBox height={24} />
        </>
      );
    return (
      <CloseIcon
        color="var(--ds-c-green-default)"
        onClick={handleRemoveDiscountApplied}
        styles={{
          cursor: 'pointer',
        }}
      />
    );
  };

  const parseAmountValue = (value) => {
    return Number.isFinite(value)
      ? format.toAmountFloatStr(numberUtils.mathRound(value))
      : '';
  };

  const renderDiscountAppliedContent = () => {
    if (isAddTransactionPage)
      return (
        <DiscountAppliedContentTO
          data-testid="discount-applied-section"
          data-code={code}
        >
          <Title>{t('label_offer')}</Title>
          <SizeBox height={4} />
          <DiscountDetailsTO>
            <img src={GiftBoxIcon} width={30} height={30} alt="" />
            <DiscountDetailsContentTO>
              <DiscountDetailsContentCodeText
                dangerouslySetInnerHTML={{
                  __html: t('label_discount_apply_details_code', {
                    code,
                  }),
                }}
                onClick={(e) => {
                  preventOnClick(e);
                  if (e.target?.tagName !== 'SPAN') return;
                  handleOnOpenDiscountModal();
                }}
              />
              <DiscountDetailsContentYouSaveText>
                {`${t('label_discount_apply_details_you_save')} ${parseAmountValue(Number(discountAmount))} AUD`}
              </DiscountDetailsContentYouSaveText>
            </DiscountDetailsContentTO>
            {!isPreventRenderRemoveDiscountButton && (
              <ArrowDownIcon
                color="var(--ds-c-blue-hyperlink-default)"
                size={{ width: 32, height: 30 }}
                styles={{
                  transform: 'rotate(-90deg)',
                  position: 'absolute',
                  right: '0px',
                  top: '13px',
                  cursor: 'pointer',
                  padding: '10px',
                }}
                onClick={onToggleNavbar}
              />
            )}
          </DiscountDetailsTO>
          {renderRemoveDiscountButton()}
        </DiscountAppliedContentTO>
      );
    return (
      <DiscountAppliedContent $locate={locate}>
        <CircleRoundCutHorizontal />
        <CircleRoundCutHorizontal right="calc(100% - 8px)" />
        <DiscountDetails onClick={handleOnOpenDiscountModal}>
          <ScaleCodeContainer $width={discountCodeContainerWidth}>
            <CodeText className={discountCodeClassName}>
              {code}
              <InfoImage
                src={InfoFillIcon}
                onClick={handleOnOpenDiscountModal}
              />
            </CodeText>
          </ScaleCodeContainer>
        </DiscountDetails>
        {renderRemoveDiscountButton()}
      </DiscountAppliedContent>
    );
  };

  return (
    <>
      {isOpenDiscountModal && discount && (
        <DiscountModal
          isOpen={isOpenDiscountModal && discount}
          discount={discount}
          isFx={isFx}
          onClose={handleOnCloseDiscountModal}
        />
      )}
      <InformPopup
        isOpen={isOpenRemoveConfirm}
        text={t('popup_remove_discount_title')}
        content={t('popup_remove_discount_desc')}
        buttonLabel1={t('button_keep')}
        onClose={handleOnCloseRemoveConfirm}
        buttonLabel2={t('button_remove_offer')}
        onClick={handleOnConfirmRemoveDiscount}
        isClickBackdropToClose={false}
        styles={{
          width: isDesktop ? '436px' : '100%',
        }}
        alignContent="center"
      />
      <DiscountAppliedStyled $isContentHeight={isAddTransactionPage}>
        {renderDiscountAppliedContent()}
      </DiscountAppliedStyled>
    </>
  );
};

// The fixed 44px is sized for the dashed-ticket variant, whose content is a
// single 44px row. The add-transaction variant renders a taller stack (title +
// gift-box row + remove link), so a fixed height under-reserves space: the
// content overflows the box -- nothing in the chain clips, so it stays painted
// -- and the next element in the Step-2 flow is laid out over the top of it,
// hiding the remove link while its own box (and therefore its tap target)
// stays where it was. Only mobile shows it because on desktop the calculator
// form is rendered up in BodySectionHeader instead of directly below the
// discount section, so nothing occupies the overflowed area.
const DiscountAppliedStyled = styled.div`
  height: ${(props) => (props.$isContentHeight ? 'auto' : '44px')};
  min-height: 44px;
  display: flex;
  align-items: center;
  margin-bottom: 0px;
`;

const DiscountAppliedContent = styled.div`
  position: relative;
  height: 44px;
  width: ${(props) => (props.$locate === VI_LANG ? '100%' : '100%')};
  border: 2px dashed var(--ds-c-green-default);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #cbffec1a;
  padding-inline: 24px;

  @media screen and (min-width: ${MOBILE_SMALL_MIN_WIDTH}px) and (max-width: ${MOBILE_SMALL_MAX_WIDTH}px) {
    padding-inline: 16px;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
  }
`;

const DiscountDetails = styled.div`
  display: flex;
  align-items: center;
`;
const ScaleCodeContainer = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  width: ${(props) => props.$width}px;
  height: 24px;
`;
const CodeText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  color: var(--ds-c-green-default);
  white-space: nowrap;
  margin-bottom: 0px;

  display: flex;
  align-items: center;
`;
const InfoImage = styled.img`
  cursor: pointer;
  margin-left: 6px;
`;

const DiscountAppliedContentTO = styled.div`
  width: 100%;
  ${displayGridCenterStartMixin};
  cursor: default;
`;

const Title = styled.p`
  ${paragraph700Mixin};
`;

const DiscountDetailsTO = styled.div`
  border-radius: 8px;
  background-color: #fff8e9;
  padding: 10px;
  display: flex;
  gap: 4px;
  width: 100%;
  position: relative;
`;
const DiscountDetailsContentTO = styled.div`
  ${displayFlexColStartCenterMixin};
`;
const DiscountDetailsContentCodeText = styled.div`
  ${paragraph500Mixin};
  color: #303c9a;
  text-align: left;

  & span {
    font-weight: 700;
    color: #0ab450;
    ${cursorPointerMixin};
    text-decoration: underline;
  }
`;
const DiscountDetailsContentYouSaveText = styled.p`
  ${paragraph500Mixin};
  ${fontSize12Mixin};
`;

const RemoveButton = styled.p`
  ${paragraph500Mixin};
  color: #ef4137;
  text-decoration: underline;
  ${cursorPointerMixin};
`;

export default DiscountApplied;
